import { useMemo } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import { BarChart3, Users, TrendingUp, Info } from 'lucide-react'
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts'

function ScoutInsights() {
  const { players } = usePlayerStore()
  const { user } = useAuthStore()

  const scoutPlayers = players.filter(p => 
    p.scout === user?.email || 
    (user?.role === 'club' && p.owner_club_id === user?.id)
  )
  
  const statsByPosition = scoutPlayers.reduce((acc, p) => {
    const pos = p.main_position || 'Unknown'
    acc[pos] = (acc[pos] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const totalGoals = scoutPlayers.reduce((sum, p) => sum + (p.goals || 0), 0)
  const totalAssists = scoutPlayers.reduce((sum, p) => sum + (p.assists || 0), 0)
  const verifiedPlayers = scoutPlayers.filter((p) => p.is_verified).length
  const totalPotential = scoutPlayers.reduce((sum, p) => sum + (p.potential || 3), 0)
  const reputationScore = scoutPlayers.length
    ? Math.min(100, Math.round((scoutPlayers.length * 4) + verifiedPlayers * 7 + totalGoals * 0.5 + totalAssists * 0.4 + totalPotential * 2))
    : 0

  const scatterGoalsData = useMemo(() => {
    return scoutPlayers
      .filter((p) => typeof p.goals === 'number' && typeof p.apps === 'number')
      .map((p) => ({
        x: p.goals,
        y: p.apps,
        z: Math.max(3, Math.min(10, (p.goals || 0) * 0.3 + (p.assists || 0) * 0.2 + (p.kp || 0) * 0.05 + (p.dr || 0) * 0.05)),
        name: p.name,
      }))
  }, [scoutPlayers])

  const scatterPassData = useMemo(() => {
    return scoutPlayers
      .filter((p) => typeof p.kp === 'number' && typeof p.shots === 'number')
      .map((p) => ({
        x: p.kp,
        y: p.shots,
        z: Math.max(3, Math.min(10, (p.kp || 0) * 0.4 + (p.dr || 0) * 0.1 + (p.goals || 0) * 0.1)),
        name: p.name,
      }))
  }, [scoutPlayers])

  const radarAttributes = useMemo(() => {
    const attributes = ['defending', 'passing', 'dribbling', 'vision', 'physical', 'speed', 'mental', 'shooting']

    const ratings = scoutPlayers.map((p) => ({
      name: p.name,
      defending: p.du || 0,
      passing: p.kp || 0,
      dribbling: p.dr || 0,
      vision: Math.round((p.kp || 0) * 0.6 + (p.du || 0) * 0.1),
      physical: p.physical || 0,
      speed: p.potential || 0,
      mental: p.mental || 0,
      shooting: Math.round((p.goals || 0) * 0.5 + (p.shots || 0) * 0.1),
    }))

    const average = attributes.reduce((acc, attr) => {
      acc[attr] = Math.round(
        ratings.reduce((sum, player) => sum + ((player as any)[attr] || 0), 0) / Math.max(1, scoutPlayers.length)
      )
      return acc
    }, {} as Record<string, number>)

    return attributes.map((attr) => ({
      attribute: attr,
      value: average[attr] / 10,
      fullMark: 10,
    }))
  }, [scoutPlayers])

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">My Insights</h2>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm">Players Logged</h3>
            <Users size={20} className="text-green-500" />
          </div>
          <p className="text-3xl font-bold text-white">{scoutPlayers.length}</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm">Scout Reputation</h3>
            <TrendingUp size={20} className="text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-white">{reputationScore}</p>
          <p className="mt-2 text-xs text-gray-500">Based on signed talent, verified strength, and impact</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm">Goals (Combined)</h3>
            <TrendingUp size={20} className="text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-white">{totalGoals}</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-400 text-sm">Verified Talent</h3>
            <BarChart3 size={20} className="text-orange-500" />
          </div>
          <p className="text-3xl font-bold text-white">{verifiedPlayers}</p>
          <p className="mt-2 text-xs text-gray-500">Players with club or league validation status</p>
        </div>
      </div>

      {/* Position Breakdown */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-4">Players by Position</h3>
        {Object.keys(statsByPosition).length === 0 ? (
          <p className="text-gray-400">No players logged yet</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(statsByPosition)
              .sort((a, b) => b[1] - a[1])
              .map(([position, count]) => (
                <div key={position} className="flex items-center justify-between">
                  <span className="text-gray-300">{position}</span>
                  <div className="flex items-center gap-2 flex-1 ml-4">
                    <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-green-500 h-full"
                        style={{
                          width: `${(count / Math.max(...Object.values(statsByPosition))) * 100}%`
                        }}
                      />
                    </div>
                    <span className="text-white font-semibold w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-4">Recent Players</h3>
        {scoutPlayers.length === 0 ? (
          <p className="text-gray-400">No players logged yet</p>
        ) : (
          <div className="space-y-2">
            {scoutPlayers
              .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
              .slice(0, 5)
              .map(player => (
                <div key={player.id} className="flex items-center justify-between p-3 bg-gray-800/30 rounded">
                  <div>
                    <p className="text-white font-semibold">{player.name}</p>
                    <p className="text-xs text-gray-500">{player.main_position} • {player.club}</p>
                  </div>
                  <p className="text-xs text-gray-400">
                    {new Date(player.created_at || '').toLocaleDateString()}
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Scatter & Radar Analytics */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">Scatter Plots</h3>
              <p className="text-gray-400 text-sm">Compare two key metrics to spot outliers across your logged players.</p>
            </div>
            <span className="text-xs uppercase tracking-[0.24em] text-gray-500">Industry standard</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[#0f1720] rounded-3xl border border-gray-800 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-sm font-bold text-white">Expected Goals vs Actual Goals</h4>
                  <p className="text-gray-500 text-xs">Compare player goal output against expected finishing opportunities.</p>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">
                  <Info size={14} />
                  <span>Hover for details</span>
                </div>
              </div>
              {scatterGoalsData.length === 0 ? (
                <p className="text-gray-500 text-sm">Not enough data to plot expected vs actual goals.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <ScatterChart>
                    <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="x" name="Expected Goals" tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis type="number" dataKey="y" name="Actual Goals" tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value) => [value, '']} />
                    <Scatter data={scatterGoalsData} fill="#38bdf8" shape="circle" />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-[#0f1720] rounded-3xl border border-gray-800 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-sm font-bold text-white">Pass Completion vs Progressive Passes</h4>
                  <p className="text-gray-500 text-xs">Track passing reliability against forward progress and chance creation.</p>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">
                  <Info size={14} />
                  <span>Hover for details</span>
                </div>
              </div>
              {scatterPassData.length === 0 ? (
                <p className="text-gray-500 text-sm">Not enough data to plot pass completion versus progressive passes.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <ScatterChart>
                    <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="x" name="Pass Completion" tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis type="number" dataKey="y" name="Progressive Passes" tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value) => [value, '']} />
                    <Scatter data={scatterPassData} fill="#a855f7" shape="circle" />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">Radar Chart</h3>
              <p className="text-gray-400 text-sm">Profile percentile rankings across a balanced attribute set.</p>
            </div>
          </div>
          {radarAttributes.length === 0 ? (
            <p className="text-gray-500 text-sm">No data available for radar analysis.</p>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <RadarChart data={radarAttributes} outerRadius="80%">
                <PolarGrid stroke="#334155" />
                <PolarAngleAxis dataKey="attribute" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 1]} tick={false} />
                <Radar name="Average" dataKey="value" stroke="#facc15" fill="#facc15" fillOpacity={0.3} />
                <Legend formatter={(value) => value} />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}

export default ScoutInsights
